import type { Language } from './types';

export const SUPPORTED_LANGUAGES: Language[] = ['ro', 'en', 'fr'];
export const DEFAULT_LANGUAGE: Language = 'ro';

export function isSupportedLanguage(lang: unknown): lang is Language {
  return typeof lang === 'string' && SUPPORTED_LANGUAGES.includes(lang as Language);
}

export function getSavedLanguage(): Language {
  try {
    if (typeof window !== 'undefined' && window.location?.search) {
      const urlParams = new URLSearchParams(window.location.search);
      const urlLang = urlParams.get('lang');
      if (isSupportedLanguage(urlLang)) return urlLang;
    }
    const saved = localStorage.getItem('language');
    if (isSupportedLanguage(saved)) return saved;
    const match = /(?:^|;\s*)language=(ro|en|fr)(?:;|$)/.exec(document.cookie);
    if (match && isSupportedLanguage(match[1])) return match[1];
  } catch {
    // Ignore storage errors
  }
  return DEFAULT_LANGUAGE;
}

export function saveLanguage(lang: Language): void {
  try {
    localStorage.setItem('language', lang);
    document.cookie = `language=${lang};path=/;max-age=31536000;SameSite=Lax`;
    window.dispatchEvent(new CustomEvent('app:language-change', { detail: lang }));
  } catch {
    // Ignore storage errors
  }
}

export function onLanguageChange(callback: (lang: Language) => void): () => void {
  const handleStorage = (e: StorageEvent) => {
    if (e.key === 'language' && isSupportedLanguage(e.newValue)) {
      callback(e.newValue);
    }
  };
  const handleCustom = (e: Event) => {
    const custom = e as CustomEvent<Language>;
    if (isSupportedLanguage(custom.detail)) {
      callback(custom.detail);
    }
  };
  window.addEventListener('storage', handleStorage);
  window.addEventListener('app:language-change', handleCustom);
  return () => {
    window.removeEventListener('storage', handleStorage);
    window.removeEventListener('app:language-change', handleCustom);
  };
}
